/**
 * Registro de actividad de "Tu camino inicial" (aceptación de recursos + preguntas al bot).
 *
 * No existe todavía una tabla para esto en el backend — a diferencia de la etapa/contenido
 * (que se infiere de data ya disponible en Psi Platform: ranking_category, process_limit,
 * status), esto sí necesita una tabla nueva del lado de api-core para tener un registro
 * durable y visible desde un admin.
 *
 * Backend endpoints to implement:
 *   POST /v1/psi-platform/psychologist/journey/accept-resource   { stage, resource_key }
 *   POST /v1/psi-platform/psychologist/journey/bot-log           { stage, question, answer }
 *
 * Mientras no existan, mismo patrón que `logChatbotQuestion` (hooks/api/admin/chatbot.ts):
 * se intenta el POST y, si falla, se guarda en localStorage. Esto funciona hoy para gatear la
 * UI (no repetir un recurso ya aceptado), pero no sincroniza entre dispositivos ni es visible
 * para un admin — eso solo lo resuelve la tabla real.
 */
import { secureHttp } from '../secureHttp';

// ─── Aceptación de recursos ───────────────────────────────────────────────────

function acceptedResourcesKey(psychologistId: string): string {
  return `psi-journey-accepted-resources-${psychologistId}`;
}

export function getAcceptedResourcesLocally(psychologistId: string): Set<string> {
  try {
    const raw = localStorage.getItem(acceptedResourcesKey(psychologistId));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveAcceptedResourceLocally(psychologistId: string, resourceKey: string): void {
  const current = getAcceptedResourcesLocally(psychologistId);
  current.add(resourceKey);
  try {
    localStorage.setItem(acceptedResourcesKey(psychologistId), JSON.stringify(Array.from(current)));
  } catch { /* noop */ }
}

export async function acceptJourneyResource(
  psychologistId: string,
  stage: number,
  resourceKey: string,
): Promise<void> {
  // Guardamos local primero — la UI no puede depender de que el endpoint ya exista.
  saveAcceptedResourceLocally(psychologistId, resourceKey);
  try {
    await secureHttp.post(
      '/v1/psi-platform/psychologist/journey/accept-resource',
      { stage, resource_key: resourceKey },
      { showSuccessToast: false, showErrorToast: false },
    );
  } catch {
    // El endpoint todavía no existe (o falló) — ya quedó registrado localmente arriba.
  }
}

// ─── Preguntas al bot ──────────────────────────────────────────────────────────

interface JourneyBotLogEntry {
  stage: number;
  question: string;
  answer: string;
  created_at: string;
}

function botLogKey(psychologistId: string): string {
  return `psi-journey-bot-log-${psychologistId}`;
}

function saveBotLogLocally(psychologistId: string, entry: JourneyBotLogEntry): void {
  try {
    const stored = JSON.parse(localStorage.getItem(botLogKey(psychologistId)) ?? '[]') as JourneyBotLogEntry[];
    stored.push(entry);
    // Guardamos las últimas 200 nomás — esto es un fallback, no un archivo histórico.
    localStorage.setItem(botLogKey(psychologistId), JSON.stringify(stored.slice(-200)));
  } catch { /* noop */ }
}

export async function logJourneyBotQuestion(
  psychologistId: string,
  stage: number,
  question: string,
  answer: string,
): Promise<void> {
  const entry: JourneyBotLogEntry = { stage, question, answer, created_at: new Date().toISOString() };
  try {
    await secureHttp.post('/v1/psi-platform/psychologist/journey/bot-log', entry, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  } catch {
    saveBotLogLocally(psychologistId, entry);
  }
}
